import { Button, Input, Prompt, Text, toast } from "@medusajs/ui"
import { useState } from "react"
import { Section } from "@/components/ui"
import { formatDate } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"
import { useAdPlatforms, useBackfillAdSpend, useRemoveAdPlatform, useSaveAdPlatform, useSaveSpendFloor, useSyncAdSpend, type AdPlatform } from "@/lib/profit"
import { Badge, Banner, DefinitionList } from "@/terabizz"

/**
 * Settings → Customer events → Ad spend: Meta Marketing API and Google Ads API connections that pull
 * spend per day, campaign, ad set and ad (daily, plus Sync now and a 90-day backfill). Credentials
 * are stored encrypted and never shown again (last 4 only); only the owner and staff with full
 * permissions may change them. Sync and backfill need analytics:write. Without a connection, spend
 * can still be uploaded as CSV (Analytics → Insights → Growth).
 *
 * "Count spend from" (owner / full permissions): spend before that day is never pulled, stored or
 * reported (Meta: 3 Nov 2026, the ad account holds the previous store's pre-launch spend). The
 * ad account's time zone and currency (read from Meta on connect and each sync) are shown, with a
 * warning when they don't match the store's.
 */
/** "2026-11-03" → "Nov 3, 2026" (noon UTC, so the day never shifts). */
const dayLabel = (d: string) => formatDate(`${d}T12:00:00Z`, false)
const floorLabel = (d: string) => `Spend before ${dayLabel(d)} is ignored (pre-launch spend from the previous store)`
const FIELDS = {
  meta: [
    { key: "access_token", label: "System-user access token", hint: "Business settings → Users → System users → Generate token, with ads_read on the ad account.", secret: true },
    { key: "ad_account_id", label: "Ad account ID", hint: "act_2401375186924844 for Tolvane Labs.", secret: false },
  ],
  google: [
    { key: "developer_token", label: "Developer token", hint: "Google Ads → Tools → API Center (manager account).", secret: true },
    { key: "client_id", label: "OAuth client ID", hint: "Google Cloud → APIs & Services → Credentials.", secret: true },
    { key: "client_secret", label: "OAuth client secret", hint: "", secret: true },
    { key: "refresh_token", label: "OAuth refresh token", hint: "From signing in once with the Google account that has access (scope adwords).", secret: true },
    { key: "customer_id", label: "Customer ID", hint: "The account with the ads, 123-456-7890.", secret: false },
    { key: "login_customer_id", label: "Login customer ID", hint: "The manager account, when access goes through one.", secret: false },
  ],
} as const

export function AdSpendCard() {
  const { data, error } = useAdPlatforms()
  if (error) return <Section title="Ad spend"><Text size="small" className="p-4 text-ui-fg-error">{errorMessage(error)}</Text></Section>
  if (!data) return null
  return (
    <Section title="Ad spend">
      <div className="flex flex-col gap-1 px-4 pt-4">
        <Text size="small" className="text-ui-fg-subtle">Spend per day, campaign, ad set and ad, for ROAS, CAC and CM after ads (Analytics → Reports → Ads and profit). Pulled every day for the last 3 days (platforms adjust recent days), or now. Without a connection, upload spend as CSV in Analytics → Insights → Growth.</Text>
      </div>
      <Platform id="meta" name="Meta" p={data.platforms.meta} manage={data.can_manage} />
      <Platform id="google" name="Google Ads" p={data.platforms.google} manage={data.can_manage} />
    </Section>
  )
}
function Platform({ id, name, p, manage }: { id: "meta" | "google"; name: string; p: AdPlatform; manage: boolean }) {
  const can = useCan()
  const save = useSaveAdPlatform()
  const remove = useRemoveAdPlatform()
  const sync = useSyncAdSpend()
  const backfill = useBackfillAdSpend()
  const floor = useSaveSpendFloor()
  const [vals, setVals] = useState<Record<string, string>>({})
  const [from, setFrom] = useState<string | null>(null)
  const [confirm, setConfirm] = useState(false)
  const fields = FIELDS[id]
  const body = Object.fromEntries(Object.entries(vals).filter(([, v]) => v.trim() !== "").map(([k, v]) => [k, v.trim()]))
  return (
    <div className="border-t border-[color:var(--k-rule)] px-4 py-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <Text size="small" weight="plus">{name}</Text>
        <Badge tone={p.connected ? "success" : "neutral"}>{p.status}</Badge>
      </div>
      {p.last_sync_ok === false && <Banner tone="warning" title={p.sync_failures > 1 ? `The last ${p.sync_failures} syncs failed` : "The last sync failed"}>{p.last_sync_error ?? "See Issues."}{p.alert_sent_at ? ` Staff were emailed ${formatDate(p.alert_sent_at)}.` : ""}</Banner>}
      {(p.warnings ?? []).length > 0 && (
        <Banner tone="warning" title="This ad account doesn't match the store">
          {p.warnings.join(" ")} Spend still syncs.
        </Banner>
      )}
      {p.spend_from && <Text size="small" className="mb-2 text-ui-fg-subtle" data-testid={`spend-floor-${id}`}>{floorLabel(p.spend_from)}.</Text>}
      <DefinitionList
        rows={[
          [id === "meta" ? "Ad account" : "Customer ID", p.account_id ?? "—"],
          ...(id === "google" ? ([["Login customer ID", p.login_customer_id ?? "—"]] as [string, string][]) : []),
          ...(p.account
            ? ([
                ["Account name", p.account.name ?? "—"],
                ["Account time zone", p.account.timezone ?? "—"],
                ["Account currency", p.account.currency ?? "—"],
                ["Account status", p.account.status_label ?? "—"],
              ] as [string, string][])
            : []),
          ["Count spend from", p.spend_from ? dayLabel(p.spend_from) : "All spend counts"],
          ...Object.entries(p.last4 ?? {}).map(([k, v]) => [fields.find((f) => f.key === k)?.label ?? k, `••••${v}`] as [string, string]),
          ["Last sync", p.last_sync_at ? `${p.last_sync_ok ? "OK" : "Failed"} · ${formatDate(p.last_sync_at)}${p.last_sync_rows != null ? ` · ${p.last_sync_rows} rows` : ""}` : "Never"],
          ...(p.backfill ? ([["Backfill", `${p.backfill.status === "running" ? "Running" : p.backfill.status === "done" ? "Done" : "Failed"} · ${p.backfill.done_days} of ${p.backfill.days} days · ${p.backfill.rows} rows${p.backfill.error ? ` · ${p.backfill.error}` : ""}`]] as [string, string][]) : []),
        ]}
      />
      {manage ? (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {fields.map((f) => (
            <label key={f.key} className="flex flex-col gap-1 text-[12px]">
              <span className="font-medium">{f.label}</span>
              <Input size="small" type={f.secret ? "password" : "text"} autoComplete="off" placeholder={f.secret && p.last4?.[f.key] ? `Saved (••••${p.last4[f.key]}); paste to replace` : f.key === "ad_account_id" ? (p.account_id ?? "") : ""} value={vals[f.key] ?? ""} onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))} />
              {f.hint && <span className="text-ui-fg-subtle">{f.hint}</span>}
            </label>
          ))}
        </div>
      ) : (
        <Text size="xsmall" className="mt-3 text-ui-fg-subtle">Only store owners or staff with full permissions can change these credentials or the day spend counts from.</Text>
      )}
      {manage && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="font-medium">Count spend from</span>
            <Input size="small" type="date" aria-label={`${name}: count spend from`} value={from ?? p.spend_from ?? ""} onChange={(e) => setFrom(e.target.value)} />
            <span className="text-ui-fg-subtle">Spend before this day is deleted and never pulled or reported (a backfill can't bring it back).</span>
          </label>
          <Button size="small" variant="secondary" disabled={from == null || from === (p.spend_from ?? "")} isLoading={floor.isPending} onClick={() => floor.mutate({ platform: id, spend_from: from || null }, { onSuccess: (r) => (setFrom(null), toast.success(from ? `${name}: spend counts from ${dayLabel(from)}${r.deleted ? ` (${r.deleted} earlier rows deleted)` : ""}` : `${name}: all spend counts`)), onError: (e) => toast.error(errorMessage(e)) })}>Save date</Button>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {manage && <Button size="small" disabled={!Object.keys(body).length} isLoading={save.isPending} onClick={() => save.mutate({ id, body }, { onSuccess: () => (setVals({}), toast.success(`${name} saved`)), onError: (e) => toast.error(errorMessage(e)) })}>Save</Button>}
        {p.connected && can("analytics:write") && <Button size="small" variant="secondary" isLoading={sync.isPending} onClick={() => sync.mutate(id, { onSuccess: (r) => (r.sync.status === "before_floor" ? toast.info(r.sync.message ?? "Nothing to sync") : toast.success(`Synced ${r.sync.rows ?? 0} rows`)), onError: () => toast.error(`${name} sync failed: see the reason above`) })}>Sync now</Button>}
        {p.connected && can("analytics:write") && <Button size="small" variant="secondary" disabled={p.backfill?.status === "running"} isLoading={backfill.isPending} onClick={() => backfill.mutate(id, { onSuccess: (r) => (r.backfill.status === "before_floor" ? toast.info(r.backfill.message ?? "Nothing to backfill") : toast.success(`Backfill started: ${r.backfill.backfill ? `${dayLabel(r.backfill.backfill.from)} to ${dayLabel(r.backfill.backfill.to)}` : "the last 90 days"}, in the background`)), onError: (e) => toast.error(errorMessage(e)) })}>Backfill 90 days</Button>}
        {manage && Object.keys(p.last4 ?? {}).length > 0 && <Button size="small" variant="transparent" onClick={() => setConfirm(true)}>Remove credentials</Button>}
      </div>
      <Prompt open={confirm} onOpenChange={setConfirm}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Remove the {name} credentials?</Prompt.Title>
            <Prompt.Description>Spend stops syncing. Spend already pulled stays in the reports.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action onClick={() => remove.mutate(id, { onSuccess: () => toast.success("Removed"), onError: (e) => toast.error(errorMessage(e)) })}>Remove</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </div>
  )
}
